/**
 * SurpriseMePage — Full-featured surprise discovery with:
 * - Photo upload (Gemini Vision analysis → search)
 * - Voice input (Web Speech API)
 * - Inline mini-chat for follow-up refinements
 * - All 3 sections stacked (Dine-Out, Order-In, Prepare-It)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, RefreshCw, Search, UtensilsCrossed,
  ShoppingBag, ChefHat, Plus, Camera, Mic, MicOff,
  MessageCircle, Send, X, ChevronUp, Loader2
} from 'lucide-react';
import FoodCard from '../components/FoodCard';
import FoodMoodQuiz from '../components/FoodMoodQuiz';
import { useLocation } from '../contexts/LocationContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PROMPTS = ["best brunch near me", "healthy dinner under $30", "easy pasta tonight", "romantic date night", "comfort food", "quick lunch", "vegan street food", "late-night ramen"];
const TABS = [
  { key: 'dine-out', label: 'Dine Out', icon: UtensilsCrossed, mode: 'dine-out', desc: 'Step out and discover something new' },
  { key: 'order-in', label: 'Order In', icon: ShoppingBag, mode: 'dine-in', desc: 'Craving hits? Get it delivered' },
  { key: 'prepare-it', label: 'Prepare It', icon: ChefHat, mode: 'cook-at-home', desc: 'Cook something exciting at home' },
];

const SurpriseMePage = () => {
  const { locationString } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem('fq_auth_token');
  const hdrs = token ? { 'Authorization': `Bearer ${token}` } : {};

  // Surprise state — init from localStorage cache for instant render
  const [sections, setSections] = useState(() => {
    try {
      const cached = localStorage.getItem('fq_surprise_sections');
      return cached ? JSON.parse(cached) : {};
    } catch { return {}; }
  });
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dine-out');
  const [tabLoading, setTabLoading] = useState({});

  // Search
  const [query, setQuery] = useState('');
  const [shaking, setShaking] = useState(false);
  const [emptyHint, setEmptyHint] = useState(false);

  // Photo upload
  const photoInputRef = useRef(null);
  const [photoAnalyzing, setPhotoAnalyzing] = useState(false);
  const [photoHint, setPhotoHint] = useState('');

  // Voice
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  // Mini-chat
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatSessionRef = useRef(`surprise_chat_${Date.now()}`);
  const chatEndRef = useRef(null);

  // Food Mood Quiz
  const [moodOpen, setMoodOpen] = useState(false);
  const [todaysMood, setTodaysMood] = useState(null);

  /* ── Fetch surprise data (parallelized) ── */
  const fetchSurprise = useCallback(async (q = '', refresh = false, tabKey = null) => {
    const targetTab = tabKey || activeTab;
    if (refresh) setRefreshing(true);
    setTabLoading(prev => ({ ...prev, [targetTab]: true }));
    try {
      const p = new URLSearchParams({ location: locationString || '', mode: targetTab });
      if (q) p.append('query', q);
      const hour = new Date().getHours();
      p.append('vibe', hour < 11 ? 'morning' : hour < 15 ? 'lunch' : hour < 18 ? 'afternoon' : hour < 22 ? 'evening' : 'late-night');
      const res = await fetch(`${API_URL}/api/surprise-me?${p}`, { credentials: 'include', headers: hdrs });
      if (res.ok) {
        const data = await res.json();
        setSections(prev => {
          const next = { ...prev, ...(data.sections || {}) };
          // Persist to localStorage for instant load on next visit
          try { localStorage.setItem('fq_surprise_sections', JSON.stringify(next)); } catch {}
          return next;
        });
      }
    } catch {} finally {
      setTabLoading(prev => ({ ...prev, [targetTab]: false }));
      setRefreshing(false);
    }
  }, [locationString, activeTab, hdrs]);

  const handleTabSwitch = useCallback((tabKey) => {
    setActiveTab(tabKey);
    document.getElementById(`section-${tabKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    TABS.forEach(t => fetchSurprise('', false, t.key));
    // Pre-cache images for visible tab via Service Worker
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      const base = API_URL;
      navigator.serviceWorker.controller.postMessage({
        type: 'PRECACHE_API',
        urls: TABS.map(t =>
          `${base}/api/surprise-me?location=${encodeURIComponent(locationString || '')}&mode=${t.key}`
        ),
      });
    }
  }, [locationString]);

  // Check for today's mood on mount
  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/food-mood/today`, { credentials: 'include', headers: hdrs })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data && data.mood_title) setTodaysMood(data); })
      .catch(() => {});
  }, []);

  /* ── Search ── */
  const doSearch = () => {
    const q = query.trim();
    if (!q) {
      setShaking(true); setEmptyHint(true);
      setTimeout(() => setShaking(false), 400);
      setTimeout(() => setEmptyHint(false), 3000);
      return;
    }
    navigate(`/ai-chat?q=${encodeURIComponent(q)}`);
  };

  const handleNewSurprises = () => {
    TABS.forEach(t => fetchSurprise('', true, t.key));
  };

  /* ── Photo Upload & Analysis ── */
  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoAnalyzing(true);
    setPhotoHint('Analyzing your photo...');
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result.split(',')[1];
        try {
          const res = await fetch(`${API_URL}/api/surprise-me/analyze-photo`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...hdrs },
            credentials: 'include',
            body: JSON.stringify({ image_base64: base64 }),
          });
          if (res.ok) {
            const data = await res.json();
            if (data.search_query) {
              setQuery(data.search_query);
              setPhotoHint(data.prompt_hint || `Found: ${data.dish_name}`);
              // Re-fetch all sections with the photo-derived query
              TABS.forEach(t => fetchSurprise(data.search_query, true, t.key));
              setTimeout(() => setPhotoHint(''), 5000);
            } else {
              setPhotoHint(data.prompt_hint || "Couldn't identify the dish — try describing it");
              setTimeout(() => setPhotoHint(''), 4000);
            }
          }
        } catch {
          setPhotoHint("Analysis failed — try again");
          setTimeout(() => setPhotoHint(''), 3000);
        }
        setPhotoAnalyzing(false);
      };
      reader.readAsDataURL(file);
    } catch {
      setPhotoAnalyzing(false);
      setPhotoHint('');
    }
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  /* ── Voice Input (Web Speech API) ── */
  const toggleVoice = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setPhotoHint('Voice input not supported in this browser');
      setTimeout(() => setPhotoHint(''), 3000);
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;

    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setQuery(transcript);
    };

    recognition.onend = () => {
      setIsListening(false);
      // Auto-search when voice ends if there's a query
      const q = query.trim();
      if (q) {
        navigate(`/ai-chat?q=${encodeURIComponent(q)}`);
      }
    };

    recognition.onerror = () => {
      setIsListening(false);
    };

    recognition.start();
    setIsListening(true);
  };

  /* ── Mini-Chat (Conversational Follow-Up) ── */
  const sendChatMessage = async () => {
    const msg = chatInput.trim();
    if (!msg || chatLoading) return;

    setChatMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatInput('');
    setChatLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/smart/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...hdrs },
        credentials: 'include',
        body: JSON.stringify({
          message: msg,
          session_id: chatSessionRef.current,
          location: locationString || '',
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const aiText = data.message || data.response || data.text || JSON.stringify(data);
        setChatMessages(prev => [...prev, { role: 'ai', text: aiText, results: data.results }]);

        // If the chat returned results, also refresh surprise sections with refined context
        if (msg.toLowerCase().includes('vegan') || msg.toLowerCase().includes('cheap') || msg.toLowerCase().includes('healthy') || msg.toLowerCase().includes('spicy')) {
          TABS.forEach(t => fetchSurprise(msg, true, t.key));
        }
      } else {
        setChatMessages(prev => [...prev, { role: 'ai', text: "Sorry, I couldn't process that. Try again!" }]);
      }
    } catch {
      setChatMessages(prev => [...prev, { role: 'ai', text: "Network error — please try again." }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20 md:pb-8">

      {/* HERO */}
      <div className="relative" style={{ minHeight: '380px' }}>
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1600&q=80')" }} />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70" />

        <div className="relative flex flex-col items-center justify-center text-center px-4 sm:px-6" style={{ minHeight: '380px' }}>
          <Link to="/" className="absolute top-5 left-5 inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm transition-colors">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>

          <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-2 tracking-tight">
            Surprise Me<span className="text-[#FF6B35]">.</span>
          </h1>
          <p className="text-white/70 text-base sm:text-lg mb-7 max-w-md">
            Tell us what you're craving{locationString ? ` in ${locationString}` : ''} — AI finds the perfect match
          </p>

          {/* ── Enhanced Search Bar with Photo + Voice ── */}
          <div className="w-full max-w-2xl">
            <div className={`flex items-center bg-white rounded-full shadow-xl overflow-hidden ${shaking ? 'animate-shake' : ''} ${emptyHint ? 'ring-2 ring-red-400' : ''}`}>
              {/* Camera/Photo upload */}
              <button
                onClick={() => photoInputRef.current?.click()}
                disabled={photoAnalyzing}
                className="ml-2 p-2.5 text-gray-400 hover:text-[#FF6B35] transition-colors rounded-full hover:bg-gray-50 flex-shrink-0"
                data-testid="photo-upload-btn"
                title="Upload a food photo"
              >
                {photoAnalyzing ? <Loader2 className="w-5 h-5 animate-spin text-[#FF6B35]" /> : <Camera className="w-5 h-5" />}
              </button>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoSelect}
                style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }}
                data-testid="photo-file-input"
              />

              <input
                type="text" value={query}
                onChange={e => { setQuery(e.target.value); setEmptyHint(false); }}
                onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
                placeholder="tacos, cheap dinner, ramen..."
                className="flex-1 h-12 sm:h-14 px-2 text-gray-800 placeholder-gray-400 text-sm sm:text-base bg-transparent focus:outline-none"
                data-testid="surprise-search-input"
              />

              {/* Voice input */}
              <button
                onClick={toggleVoice}
                className={`p-2.5 transition-colors rounded-full flex-shrink-0 ${isListening ? 'text-red-500 bg-red-50 animate-pulse' : 'text-gray-400 hover:text-[#FF6B35] hover:bg-gray-50'}`}
                data-testid="voice-input-btn"
                title={isListening ? 'Stop listening' : 'Voice search'}
              >
                {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>

              <button
                onClick={doSearch}
                className="h-10 sm:h-11 px-6 mr-1.5 bg-[#FF6B35] hover:bg-[#E5592E] text-white rounded-full flex items-center gap-2 font-bold text-sm transition-colors active:scale-95 flex-shrink-0"
                data-testid="surprise-search-btn"
              >
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">Ask AI</span>
              </button>
            </div>

            {/* Photo / Voice hints */}
            {(photoHint || emptyHint || isListening) && (
              <p className={`text-xs mt-2 animate-fade-in-up ${emptyHint ? 'text-red-300' : isListening ? 'text-green-300' : 'text-amber-300'}`} data-testid="search-hint">
                {isListening ? "Listening... speak your craving" : emptyHint ? "Tell us what you're craving!" : photoHint}
              </p>
            )}
          </div>

          {/* Prompt chips */}
          <div className="flex flex-wrap justify-center gap-2 mt-5 max-w-2xl">
            {PROMPTS.map((p, i) => (
              <button key={i} onClick={() => navigate(`/ai-chat?q=${encodeURIComponent(p)}`)}
                className="text-xs text-white/80 bg-white/15 hover:bg-white/25 backdrop-blur-sm border border-white/20 px-3.5 py-1.5 rounded-full transition-all hover:scale-105">{p}</button>
            ))}
          </div>

          {/* Food Mood Quiz entry */}
          <button
            onClick={() => setMoodOpen(true)}
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-white/20 hover:bg-white/30 backdrop-blur-md border border-white/30 text-white rounded-full transition-all hover:scale-105 active:scale-95"
            data-testid="food-mood-trigger"
          >
            <Sparkles className="w-4 h-4 text-amber-300" />
            <span className="text-sm font-bold">
              {todaysMood ? `${todaysMood.mood_emoji} ${todaysMood.mood_title}` : "What's Your Food Mood?"}
            </span>
          </button>
        </div>
      </div>

      {/* Sticky bar — tabs */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center justify-between py-2.5">
              <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
                {TABS.map(t => {
                  const Icon = t.icon; const count = sections[t.key]?.cards?.length || 0;
                  const isActive = activeTab === t.key;
                  return (
                    <button key={t.key} onClick={() => handleTabSwitch(t.key)}
                      className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium whitespace-nowrap rounded-full transition-all ${isActive ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
                      data-testid={`tab-${t.key}`}>
                      <Icon className="w-4 h-4" />{t.label}
                      {tabLoading[t.key] && <RefreshCw className="w-3 h-3 animate-spin" />}
                      {!tabLoading[t.key] && count > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${isActive ? 'bg-white/20' : 'bg-gray-100'}`}>{count}</span>}
                    </button>
                  );
                })}
              </div>

            <button
              onClick={handleNewSurprises}
              disabled={refreshing}
              data-testid="new-surprises-btn"
              className="h-9 px-4 bg-gray-900 text-white rounded-xl flex items-center gap-1.5 text-xs font-semibold hover:bg-gray-800 active:scale-95 transition-all flex-shrink-0 disabled:opacity-50 ml-2"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              New Surprises
            </button>
          </div>
        </div>
      </div>

      {/* CONTENT — All sections stacked */}
      <div className="space-y-10 py-8">
        {TABS.map(tab => {
          const section = sections[tab.key];
          const cards = section?.cards || [];
          const isLoading = tabLoading[tab.key];
          const Icon = tab.icon;

          return (
            <div key={tab.key}>
              {section?.ai_insight && !isLoading && (
                <div className="max-w-5xl mx-auto px-4 mb-4">
                  <div className="p-3.5 rounded-xl bg-white border border-gray-100 shadow-[0_1px_2px_rgba(0,0,0,0.04)] flex items-start gap-2.5">
                    <Sparkles className="w-4 h-4 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-gray-600 italic leading-relaxed">
                      "{section.ai_insight}"
                    </p>
                  </div>
                </div>
              )}

              {isLoading ? (
                <div className="max-w-7xl mx-auto px-4">
                  <div className="flex items-center gap-2.5 mb-4"><div className="w-9 h-9 rounded-xl bg-gray-100 animate-pulse" /><div className="h-5 bg-gray-100 rounded w-24 animate-pulse" /></div>
                  <div className="flex gap-4 overflow-hidden">{[1,2,3].map(j => (
                    <div key={j} className="shrink-0 w-[320px] md:w-[380px] bg-white rounded-[20px] animate-pulse shadow-sm">
                      <div className="aspect-[4/3] bg-gray-100 rounded-t-[20px]" /><div className="p-4 space-y-2"><div className="h-4 bg-gray-100 rounded w-2/3" /><div className="h-3 bg-gray-100 rounded w-1/2" /></div>
                    </div>
                  ))}</div>
                </div>
              ) : cards.length === 0 ? null : (
                <section id={`section-${tab.key}`} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 scroll-mt-16">
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <div className="w-9 h-9 rounded-xl bg-[#FFF5F0] flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#FF6B35]" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg md:text-xl font-bold text-gray-900">{tab.label}</h2>
                        <span className="text-[10px] bg-gray-100 text-gray-500 font-bold px-2 py-0.5 rounded-full">{cards.length}</span>
                      </div>
                      <p className="text-[11px] text-gray-500">{section?.theme || tab.desc}</p>
                    </div>
                  </div>

                  <div className="flex gap-4 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
                    {cards.map((card, i) => (
                      <div key={card.id || i} className="snap-start shrink-0 w-[320px] md:w-[380px] relative">
                        {card.image_source === 'ai_generated' && (
                          <span className="absolute top-4 right-4 z-10 bg-gradient-to-r from-violet-600/80 to-indigo-600/80 text-white text-[7px] font-bold px-2 py-0.5 rounded-md backdrop-blur-sm">AI Enhanced</span>
                        )}
                        <FoodCard variant="recommendation" dish={card} mode={tab.mode} />
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Floating Mini-Chat Bubble ── */}
      <div className="fixed bottom-24 left-6 z-40 md:bottom-8" data-testid="mini-chat-container">
        {chatOpen ? (
          <div className="w-[340px] max-h-[420px] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden animate-fade-in-up">
            {/* Chat header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-[#FF6B35]/5 to-transparent">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FF6B35]" />
                <span className="text-sm font-bold text-gray-800">Refine Results</span>
              </div>
              <button onClick={() => setChatOpen(false)} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Chat messages */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2.5 min-h-[200px] max-h-[280px]">
              {chatMessages.length === 0 && (
                <div className="text-center py-6 text-gray-400 text-xs">
                  <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>Ask follow-ups to refine your surprises</p>
                  <div className="flex flex-wrap justify-center gap-1.5 mt-3">
                    {["make it vegan", "cheaper options", "something spicy", "open late"].map((s, i) => (
                      <button key={i} onClick={() => { setChatInput(s); }} className="text-[10px] bg-gray-100 text-gray-500 px-2.5 py-1 rounded-full hover:bg-[#FF6B35]/10 hover:text-[#FF6B35] transition-colors">{s}</button>
                    ))}
                  </div>
                </div>
              )}
              {chatMessages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-[13px] leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-[#FF6B35] text-white rounded-br-md'
                      : 'bg-gray-100 text-gray-700 rounded-bl-md'
                  }`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 px-4 py-2 rounded-2xl rounded-bl-md">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Chat input */}
            <div className="border-t border-gray-100 p-2.5">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') sendChatMessage(); }}
                  placeholder="Make it vegan..."
                  className="flex-1 h-9 px-3.5 text-sm bg-gray-50 rounded-full border border-gray-200 focus:outline-none focus:border-[#FF6B35]/40 focus:ring-1 focus:ring-[#FF6B35]/20"
                  data-testid="mini-chat-input"
                />
                <button
                  onClick={sendChatMessage}
                  disabled={!chatInput.trim() || chatLoading}
                  className="w-9 h-9 bg-[#FF6B35] text-white rounded-full flex items-center justify-center hover:bg-[#E5592E] transition-colors disabled:opacity-40 active:scale-90"
                  data-testid="mini-chat-send"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setChatOpen(true)}
            className="w-14 h-14 bg-white text-[#FF6B35] rounded-2xl shadow-lg border border-gray-100 flex items-center justify-center hover:shadow-xl hover:scale-105 transition-all active:scale-95 group"
            data-testid="mini-chat-toggle"
            title="Ask follow-up to refine results"
          >
            <MessageCircle className="w-6 h-6 group-hover:scale-110 transition-transform" />
            <ChevronUp className="w-3 h-3 absolute -top-1 -right-1 bg-[#FF6B35] text-white rounded-full" />
          </button>
        )}
      </div>

      {/* Floating "Post New FoodCard" button */}
      <Link to="/create-foodcard"
        className="fixed bottom-24 right-6 z-30 w-14 h-14 bg-[#FF6B35] text-white rounded-2xl shadow-lg shadow-[#FF6B35]/30 flex items-center justify-center hover:bg-[#E5592E] active:scale-90 transition-all"
        data-testid="fab-create-foodcard">
        <Plus className="w-6 h-6" />
      </Link>

      {/* Food Mood Quiz Modal */}
      <FoodMoodQuiz
        open={moodOpen}
        onClose={() => {
          setMoodOpen(false);
          // Refresh today's mood after quiz
          if (token) {
            fetch(`${API_URL}/api/food-mood/today`, { credentials: 'include', headers: hdrs })
              .then(r => r.ok ? r.json() : null)
              .then(data => { if (data && data.mood_title) setTodaysMood(data); })
              .catch(() => {});
          }
        }}
        locationString={locationString}
      />
    </div>
  );
};

export default SurpriseMePage;
